import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FormSubmission } from '../../_shared/models/form/form-submission.model';
import { IFormResponse } from '../../_shared/models/form/form-response.model';

export abstract class FormClientBase {
    protected _isSubmitting = new BehaviorSubject<boolean>(false);
    isSubmitting$: Observable<boolean> = this._isSubmitting.asObservable();

    protected _success = new Subject<IFormResponse>();
    success$ = this._success.asObservable();

    protected _error = new Subject<IFormResponse>();
    error$ = this._error.asObservable();

    protected _complete = new Subject<void>();
    complete$ = this._complete.asObservable();

    protected _submission: FormSubmission = new FormSubmission();

    set submission(value: FormSubmission) {
        this._submission = value;
    }

    constructor(protected _httpClient: HttpClient) {}

    abstract submit(): void;

    protected handleError(err: HttpErrorResponse): void {
        const errorMessage = this.normalizeErrorMessage(err);
        const response: IFormResponse = { type: 'error', message: errorMessage };

        this._error.next(response);
    }

    /**
     * Normalizes an {@link HttpErrorResponse} into a user-facing error message.
     *
     * Uses the first matching value below:
     * - `statusText` when the status is 500 (if present)
     * - `err.error` when it's a string
     * - `err.error.message` when it's a string
     * - `err.error.error` when it's a string
     * - otherwise: `err.message`, then `err.statusText`, then `'Unknown error'`
     *
     * This avoids returning `[object Object]` when `err.error` is a structured payload.
     */
    protected normalizeErrorMessage(err: HttpErrorResponse): string {
        const fallback = err.message || err.statusText || 'Unknown error';

        if (err.status === 500 && err.statusText) return err.statusText;

        const error = err.error;

        if (error == null) return fallback;

        if (typeof error === 'string') return error;

        if (typeof error === 'object' && typeof error.message === 'string') return error.message;

        if (typeof error === 'object' && typeof error.error === 'string') return error.error;

        return fallback;
    }
}
